import axios from "axios";
import { API_BASE_PATH } from "../../environment";
import { toast } from "react-toastify";

const BASE_URL = `${API_BASE_PATH}`;

// ====== Error Extractor ======
const extractErrorMessage = (error: any) => {
  const detail = error?.response?.data?.detail;
  let details = error?.response?.data?.details;
  const message = error?.response?.data?.message;

  if (typeof details === "string") {
    details = details.replace(/^\d+\s*:\s*/, ""); // strip numeric codes
  }

  return detail || details || message;
};

// =============================
// Get All Enquiries
// =============================
export const getAllEnquiries = async (_token: string | null) => {
  try {
    const response = await axios.get(`${BASE_URL}/enquiries/`, {
      headers: {
        accept: "application/json",
        authorization: "Bearer " + _token,
      },
    });

    return Array.isArray(response.data) ? response.data : [];
  } catch (error: any) {
    console.error("Error fetching enquiries:", error);
    const msg = extractErrorMessage(error);
    toast.error(msg);
    throw error;
  }
};

const BASE_URL1 = `${API_BASE_PATH}/tickets`;

// =============================
// Ticket interface
// =============================
export interface Ticket {
  [x: string]: any;
  enquiry_id: string;
  ticket_title: string;
  description: string;
  Status: string;
  priority: string;
  assigned_to: string;
  assigned_employee: string;
  created_by: string;
  creator_employee: string;
  ticket_id?: string;
  enquiry?: {
    company_name: string;
    company_license: string;
    email: string;
    product_id: number;
    module_id: number;
    taken_by: string;
    requirement: string;
    category : number;
  };
  created_at: string;
}

// =============================
// GET all tickets
// =============================
export const getAllTickets = async (
  _token: string | null
): Promise<Ticket[]> => {
  try {
    const response = await axios.get(`${BASE_URL1}/`, {
      headers: {
        accept: "application/json",
        authorization: "Bearer " + _token,
      },
    });

    const raw = Array.isArray(response.data) ? response.data : [];

    return raw.map((item: any): Ticket => {
      const creatorName = item.creator_employee
        ? `${item.creator_employee.firstname} ${item.creator_employee.lastname}`
        : "";

      const assignedName = item.assigned_employee
        ? `${item.assigned_employee.firstname} ${item.assigned_employee.lastname}`
        : "";

      return {
        enquiry_id: item.enquiry_id,
        ticket_title: item.ticket_title,
        description: item.description,
        Status: item.Status,
        priority: item.priority,
        assigned_to: item.assigned_to,

        assigned_employee: assignedName,
        created_by: item.created_by,
        creator_employee: creatorName,

        ticket_id: item.ticket_id,

        enquiry: item.enquiry
          ? {
              company_name: item.enquiry.company_name,
              company_license: item.enquiry.company_license,
              email: item.enquiry.email,
              product_id: item.enquiry.product_id,
              module_id: item.enquiry.module_id,
              taken_by: item.enquiry.taken_by,
              requirement: item.enquiry.requirement,
              category: item.enquiry.category,
            }
          : undefined,

        created_at: item.created_at,
      };
    });
  } catch (error: any) {
    console.error("Error fetching tickets:", error);
    const msg = extractErrorMessage(error);
    toast.error(msg);
    throw error;
  }
};

// =============================
// POST new ticket with toast
// =============================
export const createTicket = async (
  ticketData: Omit<Ticket, "ticket_id" | "enquiry">,
  _token: string | null
): Promise<Ticket> => {
  const payloadWithStatus = {
    ...ticketData,
    status_: "Ticket Raised",
  };

  const form = new URLSearchParams();
  Object.entries(payloadWithStatus).forEach(([key, value]) => {
    form.append(key, value.toString());
  });

  try {
    const response = await axios.post(`${BASE_URL1}/create`, form, {
      headers: {
        accept: "application/json",
        authorization: "Bearer " + _token,
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
    toast.success("Ticket created successfully");
    return response.data;
  } catch (error: any) {
    console.error("Error creating ticket:", error);
    const msg = extractErrorMessage(error);
    toast.error(msg);
    throw error;
  }
};

// =============================
// Reject / Hold / Update Enquiries
// =============================
const BASE_URL_Rejection = `${API_BASE_PATH}/enquiries`;

export const rejectEnquiry = async (
  enquiryId: number,
  reason: string,
  _token: string | null
): Promise<void> => {
  const formData = new FormData();
  formData.append("rejection_reason", reason);
  formData.append("status", "Rejected");

  try {
    const response = await axios.put(
      `${BASE_URL_Rejection}/${enquiryId}`,
      formData,
      {
        headers: {
          accept: "application/json",
          authorization: "Bearer " + _token,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    toast.success("Enquiry rejected successfully");
    return response.data;
  } catch (error: any) {
    console.error("Error rejecting enquiry:", error);
    const msg = extractErrorMessage(error);
    toast.error(msg);
    throw error;
  }
};

export const holdEnquiry = async (
  enquiryId: number,
  reason: string,
  _token: string | null
): Promise<void> => {
  const formData = new FormData();
  formData.append("rejection_reason", reason);
  formData.append("status", "On Hold");

  try {
    const response = await axios.put(
      `${BASE_URL_Rejection}/${enquiryId}`,
      formData,
      {
        headers: {
          accept: "application/json",
          authorization: "Bearer " + _token,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    toast.success("Enquiry put on hold successfully");
    return response.data;
  } catch (error: any) {
    console.error("Error holding enquiry:", error);
    const msg = extractErrorMessage(error);
    toast.error(msg);
    throw error;
  }
};

export const updateEnquiry = async (
  enquiryId: number,
  _token: string | null
): Promise<void> => {
  const formData = new FormData();
  formData.append("status", "Pending To Assign");
  try {
    const response = await axios.put(
      `${BASE_URL_Rejection}/${enquiryId}`,
      formData,
      {
        headers: {
          accept: "application/json",
          authorization: "Bearer " + _token,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    toast.success("Enquiry updated successfully");
    return response.data;
  } catch (error: any) {
    console.error("Error updating enquiry:", error);
    const msg = extractErrorMessage(error);
    toast.error(msg);
    throw error;
  }
};